import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import './ApplicantForm.css';

const ApplicantForm = () => {
  const navigate = useNavigate();

  // Initial state for multiple employers
  const [employers, setEmployers] = useState([
    {
      companyName: '',
      title: '', 
      references: [{
        firstName: '',
        lastName: '',
        designation: '',
        email: '',
        phoneNumber: ''
      }]
    }
  ]);

  const [companyId, setCompanyId] = useState(null);
  const [candidateEmail, setCandidateEmail] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get('token');
    const candidateEmailFromUrl = queryParams.get('candidateEmail');
    const companyIdFromUrl = queryParams.get('companyId');

    if (token && candidateEmailFromUrl && companyIdFromUrl) {
      setCandidateEmail(candidateEmailFromUrl);
      setCompanyId(companyIdFromUrl);
    } else {
      setError('Invalid URL. Missing token, candidate email, or company ID.');
    }
  }, []);

  const handleCompanyChange = (employerIndex, e) => {
    const { name, value } = e.target;
    setEmployers(prev => prev.map((employer, idx) => 
      idx === employerIndex ? { ...employer, [name]: value } : employer
    ));
  };

  const handleReferenceChange = (employerIndex, referenceIndex, e) => {
    const { name, value } = e.target;
    setEmployers(prev => prev.map((employer, eIdx) => {
      if (eIdx === employerIndex) {
        const newReferences = employer.references.map((ref, rIdx) => 
          rIdx === referenceIndex ? { 
            ...ref, 
            [name]: name === 'phoneNumber' ? 
              value.replace(/\D/g, '').slice(0, 10) : 
              value 
          } : ref
        );
        return { ...employer, references: newReferences };
      }
      return employer;
    }));
  };

  const addEmployer = () => {
    setEmployers(prev => [...prev, {
      companyName: '',
      title: '', // Added title field
      references: [{
        firstName: '',
        lastName: '',
        designation: '',
        email: '',
        phoneNumber: ''
      }]
    }]);
  };

  const removeEmployer = (employerIndex) => {
    if (employers.length <= 1) return;
    setEmployers(prev => prev.filter((_, idx) => idx !== employerIndex));
  };

  const addReference = (employerIndex) => {
    setEmployers(prev => prev.map((employer, idx) => 
      idx === employerIndex ? {
        ...employer,
        references: [...employer.references, {
          firstName: '',
          lastName: '',
          designation: '',
          email: '',
          phoneNumber: ''
        }]
      } : employer
    ));
  };

  const removeReference = (employerIndex, referenceIndex) => {
    setEmployers(prev => prev.map((employer, eIdx) => {
      if (eIdx === employerIndex && employer.references.length > 1) {
        return {
          ...employer,
          references: employer.references.filter((_, rIdx) => rIdx !== referenceIndex)
        };
      }
      return employer;
    }));
  };

  const validateForm = () => {
    for (const employer of employers) {
      if (!employer.companyName.trim()) {
        setError('Company name is required for all employers');
        return false;
      }

      if (!employer.title.trim()) {
        setError('Title is required for all employers');
        return false;
      }

      if (employer.references.length === 0) {
        setError('At least one reference required per employer');
        return false;
      }

      for (const reference of employer.references) {
        if (!reference.firstName.trim()) {
          setError('Reference first name is required');
          return false;
        }
        if (!reference.lastName.trim()) {
          setError('Reference last name is required');
          return false;
        }
        if (!reference.designation.trim()) {
          setError('Reference title is required');
          return false;
        }
        if (!reference.email.trim()) {
          setError('Reference email is required');
          return false;
        }
        if (!/^\S+@\S+\.\S+$/.test(reference.email.trim())) {
          setError('Please enter a valid email address for all references');
          return false;
        }
        if (!reference.phoneNumber || !/^\d{10}$/.test(reference.phoneNumber)) {
          setError('Phone number must be exactly 10 digits (no symbols or spaces)');
          return false;
        }
      }
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const token = new URLSearchParams(window.location.search).get('token');
      const endpoint = `/server/companies/${companyId}/form/Addemployerdetails?candidateEmail=${encodeURIComponent(candidateEmail)}`;

      // Transform data to match backend expectations
      const requestData = employers.map(employer => ({
        companyName: employer.companyName.trim(),
        title: employer.title.trim(),
        references: employer.references.map(ref => ({
          firstName: ref.firstName.trim(),
          lastName: ref.lastName.trim(),
          designation: ref.designation.trim(),
          email: ref.email.trim(),
          phoneNumber: ref.phoneNumber.trim() 
        }))
      }));

      console.log('Final payload:', JSON.stringify(requestData, null, 2));

      const response = await api.post(endpoint, requestData, {
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${token}` 
        }
      });

      if (response.status === 200) {
        setSuccessMessage('Submission successful!');
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError(error.response?.data?.message || error.message || 'Submission failed. Please check all fields.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="form-container">
        <div className="error-box">
          <p>{error}</p>
          <button className="close-btn" onClick={() => navigate('/')}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="form-container">
        <div className="success-box">
          <p>{successMessage}</p>
          <button className="close-btn" onClick={() => navigate('/')}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <h4 className="form-title">Previous Employer References</h4>
      <form onSubmit={handleSubmit}>
        {employers.map((employer, employerIndex) => (
          <div key={`employer-${employerIndex}`} className="employer-section">
            <div className="employer-header">
              <h3>Employer #{employerIndex + 1}</h3>
              {employers.length > 1 && (
                <button
                  type="button"
                  className="remove-employer-btn"
                  onClick={() => removeEmployer(employerIndex)}
                >
                  Remove Employer
                </button>
              )}
            </div>

            <div className="form-group">
              <label>Company Name <span className="required">*</span></label>
              <input
                type="text"
                name="companyName"
                placeholder="Company Name"
                value={employer.companyName}
                onChange={(e) => handleCompanyChange(employerIndex, e)}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Your Title <span className="required">*</span></label>
              <input
                type="text"
                name="title"
                placeholder="Your position at this company"
                value={employer.title}
                onChange={(e) => handleCompanyChange(employerIndex, e)}
                required
                className="form-input"
              />
            </div>

            <h4>References</h4>
            
            {employer.references.map((reference, referenceIndex) => (
              <div key={`reference-${referenceIndex}`} className="reference-section">
                {referenceIndex > 0 && (
                  <button
                    type="button"
                    className="remove-reference-btn"
                    onClick={() => removeReference(employerIndex, referenceIndex)}
                  >
                    Remove Reference
                  </button>
                )}
                
                <div className="form-group">
                  <label>Reference First Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    value={reference.firstName}
                    onChange={(e) => handleReferenceChange(employerIndex, referenceIndex, e)}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label>Reference Last Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last Name"
                    value={reference.lastName}
                    onChange={(e) => handleReferenceChange(employerIndex, referenceIndex, e)}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label>Reference Title <span className="required">*</span></label>
                  <input
                    type="text"
                    name="designation"
                    placeholder="Title/Position"
                    value={reference.designation}
                    onChange={(e) => handleReferenceChange(employerIndex, referenceIndex, e)}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label>Reference Email <span className="required">*</span></label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={reference.email}
                    onChange={(e) => handleReferenceChange(employerIndex, referenceIndex, e)}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label>Phone Number <span className="required">*</span></label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    placeholder="1234567890 (10 digits)"
                    value={reference.phoneNumber}
                    onChange={(e) => handleReferenceChange(employerIndex, referenceIndex, e)}
                    pattern="\d{10}"
                    title="Exactly 10 digits (no symbols or spaces)"
                    required
                    className="form-input"
                  />
                </div>
              </div>
            ))}
            
            <button
              type="button"
              className="add-reference-btn"
              onClick={() => addReference(employerIndex)}
            >
              Add Another Reference
            </button>
          </div>
        ))}

        <div className="buttons-container">
          <button
            type="button"
            className="add-employer-btn"
            onClick={addEmployer}
          >
            Add Another Employer
          </button>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="submit-btn"
          >
            {isSubmitting ? 'Submitting...' : 'Submit All Information'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ApplicantForm;